import { useEffect, useState } from 'react';
import { useSocket } from '../../contexts/SocketContext';
import LogoClient from '../../assets/images/logos/logo-client.png';
import LogoPerception from '../../assets/images/logos/logo-blue-black.png';
import {
  BellIcon,
  CheckIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

const DISPLAY_STATES = {
  idle: {
    label: 'EN ATTENTE',
    description: 'Les prochaines opérations apparaîtront automatiquement ici.',
    rgb: '148 163 184',
    text: '#64748b',
    icon: ClockIcon
  },
  success: {
    label: 'ACTION VALIDÉE',
    description: 'Opération confirmée avec succès.',
    rgb: '22 163 74',
    text: '#15803d',
    icon: CheckIcon
  },
  reminder: {
    label: 'RAPPEL',
    description: 'Une information nécessite votre attention.',
    rgb: '14 165 233',
    text: '#0284c7',
    icon: BellIcon
  },
  alert: {
    label: 'ALERTE',
    description: 'Votre attention est requise.',
    rgb: '244 63 94',
    text: '#be123c',
    icon: ExclamationTriangleIcon
  },
  notification: {
    label: 'NOTIFICATION',
    description: 'Un nouvel événement a été reçu.',
    rgb: '129 140 248',
    text: '#4f46e5',
    icon: InformationCircleIcon
  }
};

const toPercentage = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.round(Math.max(0, Math.min(1, number)) * 100) : 0;
};

const formatScoreLabel = (name) => {
  const label = name.replace(/^score\s+(?:de\s+|du\s+|des\s+|d['’])/i, '');
  return label.charAt(0).toUpperCase() + label.slice(1);
};

export const ExailLivePage = () => {
  const { socket, subscribeToEvent, unsubscribeFromEvent } = useSocket();

  const [message, setMessage] = useState(null);

  // Message type: 0 = validated action (green), 1 = reminder (light blue), 2 = alert (red)
  const [messageType, setMessageType] = useState(null);
  const [analysisScores, setAnalysisScores] = useState([]);

  useEffect(() => {
    if (!socket) return undefined;

    const handleReceivedData = (data) => {
      if (data?.display_message !== undefined) setMessage(data.display_message);
      if (data?.message_type !== undefined) setMessageType(data.message_type);
      if (Array.isArray(data?.criteria)) setAnalysisScores(data.criteria);
    };

    subscribeToEvent('current_data', handleReceivedData);
    return () => unsubscribeFromEvent('current_data', handleReceivedData);
  }, [socket, subscribeToEvent, unsubscribeFromEvent]);

  const displayState = !message
    ? 'idle'
    : ({ 0: 'success', 1: 'reminder', 2: 'alert' }[messageType] ?? 'notification');

  const theme = DISPLAY_STATES[displayState];
  const Icon = theme.icon;
  const animationKey = `${displayState}-${message}`;

  const criteria = analysisScores
    .filter((criterion) => criterion && typeof criterion.name === 'string' && criterion.name.trim())
    .map((criterion) => ({
      name: criterion.name,
      label: formatScoreLabel(criterion.name),
      value: toPercentage(criterion.value),
      isValidated: criterion.is_validated === true,
      scores: (Array.isArray(criterion.scores) ? criterion.scores : [])
        .filter((score) => score && typeof score.name === 'string' && score.name.trim())
        .map((score) => ({
          name: score.name,
          label: formatScoreLabel(score.name),
          value: toPercentage(score.value)
        }))
    }));

  return (
    <div
      className="exail-live relative isolate flex h-full min-h-0 w-full items-center justify-center overflow-hidden"
      data-state={displayState}
      style={{ '--exail-rgb': theme.rgb, '--exail-text': theme.text }}
    >
      <style>{`
        .exail-live {
          background: #f7f8fb;
          color: #0f172a;
          container-type: size;
        }

        .exail-grid {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(to right, rgba(148,163,184,.085) 1px, transparent 1px),
            linear-gradient(to bottom, rgba(148,163,184,.085) 1px, transparent 1px);
          background-size: 48px 48px;
          mask-image: radial-gradient(ellipse 65% 65% at center, black, transparent);
          -webkit-mask-image: radial-gradient(ellipse 65% 65% at center, black, transparent);
          pointer-events: none;
        }

        .exail-ambient {
          position: absolute;
          border-radius: 50%;
          pointer-events: none;
          filter: blur(85px);
          will-change: transform, opacity;
        }

        .exail-ambient-primary {
          width: min(72vw, 760px);
          height: min(72vw, 760px);
          top: -24%;
          left: -12%;
          background: rgb(var(--exail-rgb) / .16);
          animation: exail-ambient-one 13s ease-in-out infinite alternate;
        }

        .exail-ambient-secondary {
          width: min(65vw, 680px);
          height: min(65vw, 680px);
          right: -14%;
          bottom: -38%;
          background: rgba(129,140,248,.13);
          animation: exail-ambient-two 17s ease-in-out infinite alternate;
        }

        .exail-ambient-center {
          width: min(65vw, 650px);
          height: min(65vw, 650px);
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          background: rgb(var(--exail-rgb) / .13);
          opacity: .6;
          animation: exail-center-breathe 7s ease-in-out infinite;
        }

        .exail-orbit {
          position: absolute;
          left: 50%;
          top: 50%;
          width: 740px;
          height: 740px;
          transform: translate(-50%, -50%);
          border: 1px solid rgba(255,255,255,.65);
          border-radius: 50%;
          box-shadow: 0 0 70px rgba(255,255,255,.25);
          pointer-events: none;
        }

        .exail-orbit::after {
          content: '';
          position: absolute;
          inset: 76px;
          border: 1px solid rgba(255,255,255,.5);
          border-radius: 50%;
        }

        .exail-card {
          position: relative;
          overflow: hidden;
          border: 1px solid rgba(255,255,255,.85);
          border-radius: 36px;
          background: rgba(255,255,255,.73);
          -webkit-backdrop-filter: blur(40px) saturate(170%);
          backdrop-filter: blur(40px) saturate(170%);
          box-shadow:
            0 32px 100px -35px rgba(15,23,42,.16),
            0 8px 30px rgba(15,23,42,.035),
            0 0 85px rgb(var(--exail-rgb) / .08),
            inset 0 1px 0 rgba(255,255,255,.9);
          transition: box-shadow .7s ease, border-color .7s ease;
        }

        .exail-main-card {
          flex: 1 1 auto;
          max-height: 410px;
          min-height: 0;
        }

        .exail-card-glow {
          position: absolute;
          inset: 0;
          pointer-events: none;
          background:
            radial-gradient(ellipse 70% 65% at 50% -25%, rgb(var(--exail-rgb) / .20), transparent 85%),
            linear-gradient(135deg, rgba(255,255,255,.75), transparent 46%);
        }

        .exail-top-shine {
          position: absolute;
          top: 0;
          left: 18%;
          right: 18%;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgb(var(--exail-rgb) / .55), transparent);
          box-shadow: 0 0 25px 2px rgb(var(--exail-rgb) / .20);
        }

        .exail-status {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 9px 15px;
          border-radius: 999px;
          border: 1px solid rgb(var(--exail-rgb) / .20);
          background: rgb(var(--exail-rgb) / .085);
          color: var(--exail-text);
          font-size: 10px;
          font-weight: 750;
          letter-spacing: .17em;
          transition: background .5s ease, border-color .5s ease;
        }

        .exail-status-dot {
          width: 7px;
          height: 7px;
          flex-shrink: 0;
          border-radius: 50%;
          background: rgb(var(--exail-rgb));
          box-shadow: 0 0 0 4px rgb(var(--exail-rgb) / .13);
          animation: exail-dot-pulse 2.4s ease-in-out infinite;
        }

        .exail-icon-aura {
          position: absolute;
          width: 160px;
          height: 160px;
          border-radius: 50%;
          background: rgb(var(--exail-rgb) / .22);
          filter: blur(30px);
          animation: exail-icon-breathe 4s ease-in-out infinite;
        }

        .exail-icon-ring {
          position: absolute;
          width: 136px;
          height: 136px;
          border-radius: 50%;
          border: 1px solid rgb(var(--exail-rgb) / .30);
          box-shadow: 0 0 20px rgb(var(--exail-rgb) / .08);
          animation: exail-ring 3.8s ease-out infinite;
        }

        .exail-icon-ring-delayed {
          animation-delay: 1.9s;
        }

        .exail-icon-core {
          position: relative;
          z-index: 1;
          display: flex;
          width: 98px;
          height: 98px;
          align-items: center;
          justify-content: center;
          border-radius: 31px;
          border: 1px solid rgb(var(--exail-rgb) / .22);
          color: var(--exail-text);
          background: linear-gradient(145deg, rgb(var(--exail-rgb) / .16), rgb(var(--exail-rgb) / .05) 58%, rgba(255,255,255,.92));
          box-shadow:
            0 16px 38px rgb(var(--exail-rgb) / .16),
            inset 0 1px 1px rgba(255,255,255,.9);
          animation: exail-icon-enter .72s cubic-bezier(.18,1.3,.32,1) both;
        }

        .exail-state-icon {
          width: 46px;
          height: 46px;
          stroke-width: 1.6;
        }

        .exail-message-reveal {
          position: relative;
          animation: exail-message-enter .95s cubic-bezier(.22,.61,.36,1) both;
          will-change: transform;
        }

        .exail-message-blurred {
          position: absolute;
          inset: 0;
          pointer-events: none;
          filter: blur(14px);
          animation: exail-text-blurred .95s linear both;
          will-change: opacity;
        }

        .exail-message-sharp {
          position: relative;
          animation: exail-text-sharp .95s linear both;
          will-change: opacity;
        }

        .exail-message {
          max-width: 670px;
          margin: 0 auto;
          font-size: clamp(1.45rem, 3.3vw, 2.9rem);
          line-height: 1.16;
          font-weight: 650;
          letter-spacing: -.045em;
          overflow-wrap: anywhere;
          white-space: pre-line;
          text-wrap: balance;
        }

        .exail-live[data-state="idle"] .exail-message {
          color: #64748b;
          font-weight: 520;
        }

        .exail-live[data-state="idle"] .exail-icon-aura {
          opacity: .4;
          animation-duration: 7s;
        }

        .exail-live[data-state="idle"] .exail-icon-ring {
          opacity: .4;
          animation-duration: 7s;
        }

        .exail-live[data-state="idle"] .exail-status-dot {
          animation-duration: 4s;
        }

        .exail-live[data-state="success"] .exail-state-icon {
          animation: exail-check .8s cubic-bezier(.2,1.5,.3,1) both;
        }

        .exail-live[data-state="reminder"] .exail-state-icon {
          transform-origin: 50% 15%;
          animation: exail-bell 4s ease-in-out infinite;
        }

        .exail-live[data-state="alert"] .exail-card {
          animation: exail-alert-glow 3.2s ease-in-out infinite;
        }

        .exail-live[data-state="alert"] .exail-state-icon {
          animation: exail-alert-icon 3s ease-in-out infinite;
        }

        .exail-live[data-state="alert"] .exail-icon-ring {
          animation-duration: 2.2s;
        }

        .exail-live[data-state="alert"] .exail-icon-ring-delayed {
          animation-delay: 1.1s;
        }

        .exail-flow-track {
          position: relative;
          height: 2px;
          width: min(245px, 70%);
          margin: 22px auto 0;
          overflow: hidden;
          border-radius: 999px;
          background: rgb(var(--exail-rgb) / .16);
        }

        .exail-flow-track::after {
          content: '';
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          width: 42%;
          border-radius: inherit;
          background: linear-gradient(90deg, transparent, rgb(var(--exail-rgb) / .95), transparent);
          animation: exail-flow 3.2s ease-in-out infinite;
        }

        .exail-live[data-state="idle"] .exail-flow-track {
          opacity: .45;
        }

        .exail-live[data-state="idle"] .exail-flow-track::after {
          animation-duration: 7s;
        }

        /* Analysis cards */

        .exail-analysis-grid {
          min-width: 0;
        }

        .exail-analysis-card {
          --analysis-bar: 148 163 184;
          --analysis-sub-bar: 180 191 205;
          position: relative;
          isolation: isolate;
          min-width: 0;
          border: 1px solid rgba(226,232,240,.85);
          border-radius: 22px;
          background: rgba(255,255,255,.72);
          -webkit-backdrop-filter: blur(30px) saturate(150%);
          backdrop-filter: blur(30px) saturate(150%);
          box-shadow:
            0 14px 40px -24px rgba(15,23,42,.12),
            0 3px 12px rgba(15,23,42,.025),
            inset 0 1px 0 rgba(255,255,255,.9);
          animation: exail-analysis-appear .55s ease-out both;
          transition:
            transform .55s cubic-bezier(.2,.8,.2,1),
            background .55s ease,
            border-color .55s ease,
            box-shadow .55s ease;
        }

        .exail-analysis-card::before {
          content: '';
          position: absolute;
          inset: 0;
          z-index: -1;
          border-radius: inherit;
          background: radial-gradient(ellipse 100% 75% at 85% 0%, rgba(74,222,128,.2), transparent 80%);
          opacity: 0;
          pointer-events: none;
          transition: opacity .6s ease;
        }

        .exail-analysis-card[data-validated="true"] {
          --analysis-bar: 34 197 94;
          --analysis-sub-bar: 74 222 128;
          z-index: 2;
          transform: translateY(-3px) scale(1.035);
          border-color: rgba(34,197,94,.38);
          background: rgba(245,255,249,.9);
          box-shadow:
            0 18px 50px -20px rgba(34,197,94,.2),
            0 0 45px rgba(34,197,94,.12),
            inset 0 1px 0 rgba(255,255,255,.95);
        }

        .exail-analysis-card[data-validated="true"]::before {
          opacity: 1;
        }

        .exail-analysis-icon {
          display: flex;
          width: 17px;
          height: 17px;
          flex-shrink: 0;
          align-items: center;
          justify-content: center;
          color: #aab6c7;
          transition: color .5s ease;
        }

        .exail-analysis-card[data-validated="true"] .exail-analysis-icon {
          color: #22c55e;
        }

        .exail-analysis-score {
          color: #64748b;
          font-variant-numeric: tabular-nums;
          transition: color .5s ease;
        }

        .exail-analysis-card[data-validated="true"] .exail-analysis-score {
          color: #15803d;
        }

        .exail-analysis-track {
          position: relative;
          width: 100%;
          overflow: hidden;
          border-radius: 999px;
          background: rgba(148,163,184,.18);
        }

        .exail-analysis-track-main {
          height: 8px;
        }

        .exail-analysis-track-sub {
          height: 4px;
        }

        .exail-analysis-fill {
          height: 100%;
          border-radius: inherit;
          background: rgb(var(--analysis-bar));
          transition:
            width .75s cubic-bezier(.22,1,.36,1),
            background .5s ease;
        }

        .exail-analysis-track-sub .exail-analysis-fill {
          background: rgb(var(--analysis-sub-bar));
        }

        .exail-analysis-card[data-validated="true"] .exail-analysis-track {
          background: rgba(34,197,94,.13);
        }

        .exail-analysis-check {
          animation: exail-analysis-check-enter .55s cubic-bezier(.18,1.3,.32,1) both;
        }

        @keyframes exail-analysis-appear {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes exail-analysis-check-enter {
          from { opacity: 0; transform: scale(.5) rotate(-20deg); }
          to { opacity: 1; transform: scale(1) rotate(0); }
        }

        @keyframes exail-ambient-one {
          from { transform: translate3d(0,0,0) scale(1); opacity: .65; }
          to { transform: translate3d(12%,16%,0) scale(1.2); opacity: 1; }
        }

        @keyframes exail-ambient-two {
          from { transform: translate3d(0,0,0) scale(1); opacity: .55; }
          to { transform: translate3d(-16%,-12%,0) scale(1.18); opacity: .9; }
        }

        @keyframes exail-center-breathe {
          0%, 100% { transform: translate(-50%,-50%) scale(.9); opacity: .35; }
          50% { transform: translate(-50%,-50%) scale(1.3); opacity: .8; }
        }

        @keyframes exail-dot-pulse {
          0%, 100% { opacity: .55; transform: scale(.85); }
          50% { opacity: 1; transform: scale(1.15); }
        }

        @keyframes exail-icon-breathe {
          0%, 100% { transform: scale(.85); opacity: .35; }
          50% { transform: scale(1.2); opacity: .8; }
        }

        @keyframes exail-ring {
          0% { transform: scale(.78); opacity: 0; }
          25% { opacity: .8; }
          100% { transform: scale(1.52); opacity: 0; }
        }

        @keyframes exail-icon-enter {
          from { opacity: 0; transform: scale(.62) translateY(12px); filter: blur(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); filter: blur(0); }
        }

        @keyframes exail-message-enter {
          from { transform: translateY(22px) scale(.98); }
          to { transform: translateY(0) scale(1); }
        }

        @keyframes exail-text-blurred {
          0% { opacity: 0; }
          10% { opacity: .75; }
          25% { opacity: .55; }
          45% { opacity: 0; }
          100% { opacity: 0; }
        }

        @keyframes exail-text-sharp {
          0% { opacity: 0; }
          12% { opacity: .15; }
          28% { opacity: .65; }
          45% { opacity: 1; }
          100% { opacity: 1; }
        }

        @keyframes exail-check {
          0% { opacity: 0; transform: scale(.4) rotate(-25deg); }
          65% { opacity: 1; transform: scale(1.2) rotate(4deg); }
          100% { opacity: 1; transform: scale(1) rotate(0); }
        }

        @keyframes exail-bell {
          0%, 72%, 100% { transform: rotate(0); }
          78% { transform: rotate(-12deg); }
          84% { transform: rotate(10deg); }
          90% { transform: rotate(-5deg); }
          95% { transform: rotate(3deg); }
        }

        @keyframes exail-alert-icon {
          0%, 75%, 100% { transform: scale(1); }
          80% { transform: scale(1.12); }
          86% { transform: scale(.96); }
          92% { transform: scale(1.05); }
        }

        @keyframes exail-alert-glow {
          0%, 100% {
            box-shadow: 0 32px 100px -35px rgba(15,23,42,.16), 0 0 45px rgb(var(--exail-rgb) / .08);
          }
          50% {
            box-shadow: 0 32px 100px -35px rgba(15,23,42,.16), 0 0 100px rgb(var(--exail-rgb) / .22);
          }
        }

        @keyframes exail-flow {
          from { transform: translateX(-110%); }
          to { transform: translateX(350%); }
        }

        @container (max-height: 680px) {
          .exail-main-card { max-height: 345px; }
          .exail-main-content { padding-top: 12px; padding-bottom: 12px; }
          .exail-icon-stage { height: 86px; margin-top: 8px; margin-bottom: 10px; }
          .exail-icon-core { width: 76px; height: 76px; border-radius: 24px; }
          .exail-state-icon { width: 36px; height: 36px; }
          .exail-icon-ring { width: 112px; height: 112px; }
          .exail-icon-aura { width: 130px; height: 130px; }
          .exail-message { font-size: clamp(1.35rem, 2.9vw, 2.5rem); }
          .exail-flow-track { margin-top: 16px; }
          .exail-analysis-card { padding: 10px 12px; }
        }

        @container (max-height: 520px) {
          .exail-main-card { max-height: 265px; }
          .exail-main-content { padding-top: 8px; padding-bottom: 8px; }
          .exail-icon-stage { height: 60px; margin-top: 6px; margin-bottom: 6px; }
          .exail-icon-core { width: 56px; height: 56px; border-radius: 18px; }
          .exail-state-icon { width: 27px; height: 27px; }
          .exail-icon-ring { width: 88px; height: 88px; }
          .exail-icon-aura { width: 105px; height: 105px; }
          .exail-message { font-size: clamp(1.1rem, 2.4vw, 1.85rem); }
          .exail-message-reveal p { margin-top: 7px; font-size: 11px; }
          .exail-flow-track { margin-top: 10px; }
          .exail-analysis-card { padding: 8px 10px; }
        }

        @container (max-height: 430px) {
          .exail-message-reveal p { display: none; }
          .exail-flow-track { display: none; }
          .exail-status { padding: 6px 10px; }
        }

        @container (max-width: 900px) {
          .exail-analysis-card { padding: 10px; }
          .exail-analysis-title { font-size: 10px; }
          .exail-analysis-score { font-size: 17px; }
          .exail-analysis-sub-label { font-size: 9px; }
        }

        @media (max-width: 640px) {
          .exail-orbit { width: 500px; height: 500px; }
          .exail-card { border-radius: 28px; }
          .exail-message { letter-spacing: -.035em; }
          .exail-analysis-card { border-radius: 15px; }
        }

        @media (prefers-reduced-motion: reduce) {
          .exail-live *, .exail-live *::before, .exail-live *::after {
            animation-duration: .01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: .01ms !important;
          }
        }
      `}</style>

      <div className="exail-grid" aria-hidden="true" />
      <div className="exail-ambient exail-ambient-primary" aria-hidden="true" />
      <div className="exail-ambient exail-ambient-secondary" aria-hidden="true" />
      <div className="exail-ambient exail-ambient-center" aria-hidden="true" />
      <div className="exail-orbit" aria-hidden="true" />

      <main className="relative z-10 mx-auto flex h-full min-h-0 w-full max-w-[1220px] flex-col px-5 py-3 sm:px-8 sm:py-4">
        <div className="mx-auto flex min-h-0 w-full max-w-[840px] flex-1 flex-col justify-center">
          <div className="mb-4 flex shrink-0 flex-wrap items-center justify-between gap-3 px-2">
            <div className="flex min-w-0 items-center gap-3 sm:gap-5">
              <img
                src={LogoClient}
                alt="Exail"
                className="h-8 w-auto max-w-[100px] object-contain sm:h-10 sm:max-w-[125px]"
              />

              <span className="h-7 w-px shrink-0 bg-slate-200/80 sm:h-8" aria-hidden="true" />

              <img
                src={LogoPerception}
                alt="Perception"
                className="h-7 w-auto max-w-[110px] object-contain sm:h-9 sm:max-w-[150px]"
              />
            </div>

            <div className="flex items-center gap-2 rounded-full border border-white/80 bg-white/60 px-4 py-2 shadow-sm backdrop-blur-xl">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-lime-400 opacity-40" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-lime-400 shadow-[0_0_12px_rgba(163,230,53,0.85)]" />
              </span>
              <span className="text-[10px] font-semibold tracking-[.13em] text-slate-500">
                TEMPS RÉEL
              </span>
            </div>
          </div>

          <section className="exail-card exail-main-card">
            <div className="exail-card-glow" aria-hidden="true" />
            <div className="exail-top-shine" aria-hidden="true" />

            <div className="exail-main-content relative flex h-full min-h-0 flex-col items-center justify-center px-6 py-4 text-center sm:px-12 sm:py-5">
              <div className="exail-status">
                <span className="exail-status-dot" aria-hidden="true" />
                {theme.label}
              </div>

              <div
                className="exail-icon-stage relative my-3 flex h-28 w-36 shrink-0 items-center justify-center sm:my-4 sm:h-32 sm:w-40"
                aria-hidden="true"
              >
                <div className="exail-icon-aura" />
                <div className="exail-icon-ring" />
                <div className="exail-icon-ring exail-icon-ring-delayed" />

                <div key={animationKey} className="exail-icon-core">
                  <Icon className="exail-state-icon" />
                </div>
              </div>

              <div
                key={animationKey}
                className="exail-message-reveal w-full"
                role={displayState === 'alert' ? 'alert' : 'status'}
                aria-live={displayState === 'alert' ? 'assertive' : 'polite'}
                aria-atomic="true"
              >
                <div className="exail-message-blurred" aria-hidden="true">
                  <h1 className="exail-message">
                    {message || "En attente d'opération à analyser"}
                  </h1>
                  <p className="mx-auto mt-3 max-w-lg text-sm font-normal leading-relaxed tracking-[.005em] text-slate-400 sm:text-[15px]">
                    {theme.description}
                  </p>
                </div>

                <div className="exail-message-sharp">
                  <h1 className="exail-message">
                    {message || "En attente d'opération à analyser"}
                  </h1>
                  <p className="mx-auto mt-3 max-w-lg text-sm font-normal leading-relaxed tracking-[.005em] text-slate-400 sm:text-[15px]">
                    {theme.description}
                  </p>
                </div>
              </div>

              <div className="exail-flow-track" aria-hidden="true" />
            </div>
          </section>
        </div>

        {/* Real-time analysis criteria */}
        {criteria.length > 0 && (
          <section
            className="exail-analysis-grid mt-4 grid shrink-0 gap-3"
            style={{ gridTemplateColumns: `repeat(${criteria.length}, minmax(0, 1fr))` }}
            aria-label="Scores d'analyse en temps réel"
          >
            {criteria.map((criterion) => (
              <article
                key={criterion.name}
                className="exail-analysis-card p-3 sm:p-4"
                data-validated={criterion.isValidated}
              >
                <div className="relative z-10">
                  {/* Main score */}
                  <div className="mb-2 flex min-h-[34px] items-center justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="exail-analysis-icon">
                        {criterion.isValidated ? (
                          <CheckCircleIcon className="exail-analysis-check h-[17px] w-[17px]" />
                        ) : (
                          <ClockIcon className="h-[17px] w-[17px]" />
                        )}
                      </span>

                      <h2 className="exail-analysis-title min-w-0 text-[12px] font-semibold leading-[1.25] tracking-[-.015em] text-slate-700">
                        {criterion.label}
                      </h2>
                    </div>

                    <span className="exail-analysis-score shrink-0 text-[22px] font-bold leading-none tracking-[-.05em]">
                      {criterion.value}%
                    </span>
                  </div>

                  <div
                    className="exail-analysis-track exail-analysis-track-main"
                    role="progressbar"
                    aria-label={`${criterion.label} - score global`}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={criterion.value}
                  >
                    <div className="exail-analysis-fill" style={{ width: `${criterion.value}%` }} />
                  </div>

                  {/* Sub-scores */}
                  <div className="mt-3 flex flex-col gap-2.5">
                    {criterion.scores.map((score) => (
                      <div key={score.name} className="min-w-0">
                        <div className="mb-1 flex items-center justify-between gap-2">
                          <span className="exail-analysis-sub-label min-w-0 text-[11px] font-medium leading-tight text-slate-500">
                            {score.label}
                          </span>

                          <span className="shrink-0 text-[11px] font-semibold tabular-nums text-slate-500">
                            {score.value}%
                          </span>
                        </div>

                        <div
                          className="exail-analysis-track exail-analysis-track-sub"
                          role="progressbar"
                          aria-label={`${criterion.label} - ${score.label}`}
                          aria-valuemin={0}
                          aria-valuemax={100}
                          aria-valuenow={score.value}
                        >
                          <div className="exail-analysis-fill" style={{ width: `${score.value}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </section>
        )}
      </main>
    </div>
  );
};
